import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Star, MapPin, Clock, Sparkles, Bookmark, GitCompare, SlidersHorizontal } from "lucide-react";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import { Card } from "@/app/ui";
import { QUALIFICATION_LEVELS, STUDY_MODES, providerInitials } from "@/lib/opportunities";

const CATS = ["All", "Higher Education", "Apprenticeships", "Professional", "Online Learning", "Training"];
const LOCATIONS = ["All", "Online", "Manchester", "Leeds", "Edinburgh", "Preston"];

export default function Discover() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [level, setLevel] = useState("All");
  const [mode, setMode] = useState("All");
  const [location, setLocation] = useState("All");
  const [fundingOnly, setFundingOnly] = useState(false);
  const [internationalOnly, setInternationalOnly] = useState(false);
  const [sort, setSort] = useState("match");
  const [data, setData] = useState(null);
  const [compareCount, setCompareCount] = useState(0);
  const [busySlug, setBusySlug] = useState("");
  const isLearner = user?.role === "individual";

  const loadCompareCount = useCallback(() => {
    if (!isLearner) return;
    api.get("/individual/compare-opportunities").then((r) => setCompareCount(r.data.count)).catch(() => {});
  }, [isLearner]);

  useEffect(() => { loadCompareCount(); }, [loadCompareCount]);

  useEffect(() => {
    const params = { sort };
    if (q) params.q = q;
    if (cat !== "All") params.category = cat;
    if (level !== "All") params.qualification_level = level;
    if (mode !== "All") params.study_mode = mode;
    if (location !== "All") params.location = location;
    if (fundingOnly) params.funding = "true";
    if (internationalOnly) params.international = "true";
    const timer = setTimeout(() => {
      api.get("/opportunities", { params }).then((r) => setData(r.data)).catch(() => setData({ count: 0, opportunities: [] }));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, cat, level, mode, location, fundingOnly, internationalOnly, sort]);

  const updateOpportunity = (slug, patch) => {
    setData((current) => ({
      ...current,
      opportunities: current.opportunities.map((item) => item.slug === slug ? { ...item, ...patch } : item),
    }));
  };

  const toggleSave = async (opportunity) => {
    if (!isLearner) return;
    setBusySlug(opportunity.slug);
    try {
      const response = opportunity.is_saved
        ? await api.delete(`/individual/opportunities/${opportunity.slug}/save`)
        : await api.post(`/individual/opportunities/${opportunity.slug}/save`);
      updateOpportunity(opportunity.slug, { is_saved: response.data.saved });
      toast.success(response.data.saved ? "Saved to your opportunities." : "Removed from saved opportunities.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusySlug("");
    }
  };

  const toggleCompare = async (opportunity) => {
    if (!isLearner) return;
    setBusySlug(opportunity.slug);
    try {
      const response = opportunity.in_compare
        ? await api.delete(`/individual/opportunities/${opportunity.slug}/compare`)
        : await api.post(`/individual/opportunities/${opportunity.slug}/compare`);
      updateOpportunity(opportunity.slug, { in_compare: response.data.in_compare });
      loadCompareCount();
      toast.success(response.data.in_compare ? "Added to comparison." : "Removed from comparison.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusySlug("");
    }
  };

  return (
    <div data-testid="discover-page" className="pb-16">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Discover opportunities</h1>
          <p className="mt-2 text-slatey">Explore structured education and professional development matched to your goals.</p>
        </div>
        {isLearner && (
          <div className="flex flex-wrap gap-2">
            <Link to="/app/assessments" data-testid="discover-assessments-link" className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink hover:border-cobalt hover:text-cobalt">
              Eligibility assessments
            </Link>
            <Link to="/app/saved" data-testid="discover-saved-link" className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink hover:border-cobalt hover:text-cobalt">
              Saved opportunities
            </Link>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <Search className="h-5 w-5 text-slatey" />
        <input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses, providers or skills…"
          className="w-full bg-transparent text-[15px] outline-none placeholder:text-slatey" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase().replace(/\s/g, "-")}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-cobalt text-white" : "border border-slate-200 bg-white text-ink hover:bg-surface"}`}>{c}</button>
        ))}
      </div>

      <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-4">
        <div className="mb-4 flex items-center gap-2 text-sm font-bold text-ink"><SlidersHorizontal className="h-4 w-4 text-cobalt" /> Refine results</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">Level
            <select data-testid="discover-level-filter" value={level} onChange={(e) => setLevel(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-ink outline-none focus:border-cobalt">
              <option>All</option>
              {QUALIFICATION_LEVELS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">Study mode
            <select data-testid="discover-mode-filter" value={mode} onChange={(e) => setMode(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-ink outline-none focus:border-cobalt">
              <option>All</option>
              {STUDY_MODES.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">Location
            <select data-testid="discover-location-filter" value={location} onChange={(e) => setLocation(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-ink outline-none focus:border-cobalt">
              {LOCATIONS.map((item) => <option key={item}>{item}</option>)}
            </select>
          </label>
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-slatey">Sort
            <select data-testid="discover-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-ink outline-none focus:border-cobalt">
              <option value="match">Best match</option>
              <option value="newest">Newest</option>
              <option value="title">Title A–Z</option>
            </select>
          </label>
          <div className="flex items-end gap-3 pb-1">
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input data-testid="discover-funding-filter" type="checkbox" checked={fundingOnly} onChange={(e) => setFundingOnly(e.target.checked)} className="h-4 w-4 accent-cobalt" /> Funding
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input data-testid="discover-international-filter" type="checkbox" checked={internationalOnly} onChange={(e) => setInternationalOnly(e.target.checked)} className="h-4 w-4 accent-cobalt" /> International
            </label>
          </div>
        </div>
      </div>

      <p className="mt-6 text-sm font-semibold text-slatey" data-testid="discover-count">{data ? `${data.count} opportunities` : "Loading…"}</p>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {data?.opportunities.map((o) => (
          <Card key={o.slug} data-testid={`opp-${o.slug}`} className="p-0 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                {o.provider_logo ? (
                  <img src={o.provider_logo} alt="" className="h-12 w-12 rounded-2xl border border-slate-200 object-cover" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 text-cobalt font-display font-bold">
                    {providerInitials(o.provider)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {o.match != null && (
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-cobalt" data-testid={`match-${o.slug}`}><Sparkles className="h-4 w-4" /> {o.match}% match</p>
                  )}
                  {o.match_reasons?.length > 0 && (
                    <p className="mt-0.5 text-xs text-slatey" data-testid={`match-reason-${o.slug}`}>Why: {o.match_reasons[0]}</p>
                  )}
                  {o.match != null && <p className="mt-1 text-[11px] font-semibold text-slatey">Match is not confirmed eligibility.</p>}
                  <h3 className="mt-0.5 font-display text-xl font-bold tracking-tight text-ink">{o.title}</h3>
                  <p className="text-sm text-slatey">{o.provider}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(o.tags || []).map((t) => <span key={t} className="rounded-full bg-surface px-3 py-1 text-xs font-medium text-ink">{t}</span>)}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-y-2 text-sm text-slatey sm:grid-cols-2">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {o.location}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {o.duration}</span>
                <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400" /> {o.rating} ({o.reviews})</span>
                <span className="font-semibold text-ink">{o.fees}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 p-4">
              <Link to={`/opportunities/${o.slug}`} data-testid={`view-${o.slug}`}
                className="flex-1 rounded-xl bg-cobalt px-4 py-3 text-center text-sm font-semibold text-white transition-transform hover:scale-[1.01]">Discover</Link>
              <Link to={`/eligibility/${o.slug}`} data-testid={`check-eligibility-${o.slug}`}
                className="flex-1 rounded-xl border border-cobalt px-4 py-3 text-center text-sm font-semibold text-cobalt transition-colors hover:bg-cobalt/5">Check eligibility</Link>
              {isLearner && (
                <>
                  <button onClick={() => toggleCompare(o)} disabled={busySlug === o.slug} data-testid={`compare-${o.slug}`} aria-pressed={o.in_compare}
                    className={`rounded-xl border p-3 transition-colors ${o.in_compare ? "border-cobalt bg-cobalt/10 text-cobalt" : "border-slate-200 text-slatey hover:text-ink"}`} aria-label={`Compare ${o.title}`}>
                    <GitCompare className="h-4 w-4" />
                  </button>
                  <button onClick={() => toggleSave(o)} disabled={busySlug === o.slug} data-testid={`save-${o.slug}`} aria-pressed={o.is_saved}
                    className={`rounded-xl border p-3 transition-colors ${o.is_saved ? "border-cobalt bg-cobalt/10 text-cobalt" : "border-slate-200 text-cobalt"}`} aria-label={`Save ${o.title}`}>
                    <Bookmark className={`h-4 w-4 ${o.is_saved ? "fill-current" : ""}`} />
                  </button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {data?.opportunities.length === 0 && (
        <Card data-testid="discover-empty" className="mt-6 text-center">
          <h2 className="font-display text-2xl font-bold text-ink">No opportunities found</h2>
          <p className="mt-2 text-sm text-slatey">Try removing a filter or searching for a broader subject.</p>
        </Card>
      )}

      {isLearner && compareCount > 0 && (
        <div data-testid="compare-bar" className="fixed inset-x-4 bottom-24 z-30 mx-auto flex max-w-xl items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl lg:bottom-6">
          <p className="text-sm font-bold text-ink">{compareCount} selected for comparison</p>
          <button onClick={() => navigate("/app/compare")} data-testid="open-compare-button" className="rounded-full bg-cobalt px-4 py-2 text-sm font-bold text-white">Compare</button>
        </div>
      )}
    </div>
  );
}
